import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { AccountUserSelectBox } from '../../../admin/components/user/AccountUserSelectBox';
import { UserViewModel, isAdmin } from '../../../extViewmodels';
import { ImpersonationContextKey } from '../../../models';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useUsersWithSocialMediaConnection } from '../../../queries/User/useUsersWithSocialMediaConnection';
import { Button } from '../../components-v2/Button';
import { ColorPicker } from '../../components-v2/ColorPicker';
import { UnifiedImageDialog } from '../../components-v2/UnifiedImageDialog';
import { Checkbox } from '../../components/Checkbox';
import { useCreateEditSocialMediaContext } from '../../containers/socialMedia/EditSocialMediaPost/context';
import { ISelectOption } from '../DeprecatedSelect';
import { UserSelectBox } from '../UserSelectBox';
import { DEFAULT_COLORS, MAX_CONTENT_LENGTH } from './constants';
import { styleSheet } from './styles';
import { TestimonialControlsProps } from './types';
import { TESTIMONIAL_OVERLAY_COLOR, TESTIMONIAL_OVERLAY_OPACITY, addColorOverlayToImage } from './utils';

const IMAGES_FOR_SELECTION = [
	{ imagePath: '/static/images/googleReview/bitmap-1.png', slug: 'testimonial-1' },
	{ imagePath: '/static/images/googleReview/bitmap-2.png', slug: 'testimonial-2' },
	{ imagePath: '/static/images/googleReview/bitmap-3.png', slug: 'testimonial-3' },
];

const _TestimonialControls = ({
	bgColor,
	textColor,
	reviewText,
	authorName,
	includeGoogleLogo,
	includeAuthor,
	includeStars,
	sourceType,
	isContentTooLong,
	createPostDisabled,
	onBgColorChange,
	onTextColorChange,
	onReviewTextChange,
	onAuthorNameChange,
	onIncludeGoogleLogoChange,
	onIncludeAuthorChange,
	onIncludeStarsChange,
	onGenerateImage,
	impersonationContext,
	onSendFromChange,
	onBackgroundImageChange,
	initialBackgroundImage,
	builderStyles,
}: TestimonialControlsProps) => {
	const userSession = useUserSession();
	const usersWithSocial = useUsersWithSocialMediaConnection({
		transformer: users => users.map(user => new UserViewModel(userSession, user)),
		userSession,
	});
	const [backgroundImageModalOpen, setBackgroundImageModalOpen] = useState(false);

	const [selectedBackgroundImage, setSelectedBackgroundImage] = useState<{
		slug: string;
		fileAttachment: Api.IFileAttachment;
	} | null>(initialBackgroundImage ?? null);

	useEffect(() => {
		onBackgroundImageChange(selectedBackgroundImage);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedBackgroundImage]);

	useEffect(() => {
		if (initialBackgroundImage) {
			setSelectedBackgroundImage(initialBackgroundImage);
		}
	}, [initialBackgroundImage]);

	let socialUserOptions: ISelectOption<string | UserViewModel>[] = [];
	const [context] = useCreateEditSocialMediaContext();

	if (usersWithSocial?.data?.length > 0) {
		socialUserOptions = usersWithSocial.data
			.filter(x => x.id !== userSession?.user?.id)
			.filter(x => x.id !== context.selectedCreator?.id)
			.map<ISelectOption>(x => {
				return {
					dataContext: x,
					id: `option-${x.id}-social`,
					text: `${x.firstName} ${x.lastName}`,
				};
			});
	}

	const onSendFromChangeOption = (option: ISelectOption<string | UserViewModel>) => {
		if (option.dataContext instanceof UserViewModel) {
			onSendFromChange(option.dataContext);
		}
	};

	const hasSendOnBehalfEnabled = (impersonationContext?.isValid ? impersonationContext.account : userSession.account)
		.preferences?.sendOnBehalfEnabled;

	const isUserAdmin = isAdmin(userSession.user);

	const canSendOnBehalf = hasSendOnBehalfEnabled && isUserAdmin;

	return (
		<div className={css(styleSheet.controlsPane, ...(builderStyles ?? []))}>
			<div className={css(styleSheet.contentContainer)}>
				<h3 className={css(styleSheet.subtitle)}>Customize Testimonial</h3>
				<span className={css(styleSheet.description)}>Share your testimonials on social media</span>

				{canSendOnBehalf && (
					<div className={css(styleSheet.postFrom)}>
						<span>Post From:</span>
						<div className={css(styleSheet.userDropdown)}>
							{impersonationContext ? (
								<AccountUserSelectBox
									defaultOption={null}
									onOptionSelected={onSendFromChangeOption}
									onRenderPlaceholder={() => <span>Select User</span>}
									adminsFilter={x => x.socialMediaConnectedAccounts?.length > 0}
									initialSelectedOption={null}
								/>
							) : (
								<UserSelectBox
									defaultOption={null}
									includeSearch={false}
									onOptionSelected={onSendFromChangeOption}
									onRenderPlaceholder={() => <span>Select User</span>}
									initialSelectedOption={null}
									additionalPinnedOptions={socialUserOptions}
								/>
							)}
						</div>
					</div>
				)}

				<div className={css(styleSheet.optionsSection)}>
					<div className={css(styleSheet.colorPickersContainer)}>
						<div className={css(styleSheet.colorPickerGroup)}>
							<label className={css(styleSheet.label)}>Background Color</label>
							<div className={css(styleSheet.colorPickerWrapper)}>
								<ColorPicker value={bgColor || DEFAULT_COLORS.background} onChange={onBgColorChange} />
							</div>
						</div>

						<div className={css(styleSheet.colorPickerGroup)}>
							<label className={css(styleSheet.label)}>Text Color</label>
							<div className={css(styleSheet.colorPickerWrapper)}>
								<ColorPicker value={textColor || DEFAULT_COLORS.text} onChange={onTextColorChange} />
							</div>
						</div>
					</div>
				</div>

				<div className={css(styleSheet.optionsSection)}>
					<div className={css(styleSheet.formGroup, styleSheet.backgroundImageContainer)}>
						{!selectedBackgroundImage ? (
							<Button
								variant='link'
								onClick={() => setBackgroundImageModalOpen(true)}
								styles={[styleSheet.addBackgroundImageButton]}
							>
								+ Add Background Image
							</Button>
						) : (
							<div className={css(styleSheet.backgroundImagePreviewContainer)}>
								<img
									src={selectedBackgroundImage.fileAttachment.url}
									alt={selectedBackgroundImage.slug}
									className={css(styleSheet.backgroundImagePreview)}
								/>
								<Button variant='secondary' onClick={() => setBackgroundImageModalOpen(true)}>
									Edit
								</Button>
								<Button variant='destructive-secondary' onClick={() => setSelectedBackgroundImage(null)}>
									Remove
								</Button>
							</div>
						)}
					</div>
				</div>

				<div className={css(styleSheet.optionsSection)}>
					<div className={css(styleSheet.checkboxGrid)}>
						{sourceType === 'google_review' && (
							<Checkbox
								checked={includeGoogleLogo}
								onChange={onIncludeGoogleLogoChange}
								childrenPosition='right'
								className={css(styleSheet.checkbox)}
							>
								<label className={css(styleSheet.checkboxLabel)}>Include Google Logo</label>
							</Checkbox>
						)}
						<Checkbox
							checked={includeStars}
							onChange={onIncludeStarsChange}
							childrenPosition='right'
							className={css(styleSheet.checkbox)}
						>
							<label className={css(styleSheet.checkboxLabel)}>Include Stars</label>
						</Checkbox>
						<Checkbox
							checked={includeAuthor}
							onChange={onIncludeAuthorChange}
							childrenPosition='right'
							className={css(styleSheet.checkbox)}
						>
							<label className={css(styleSheet.checkboxLabel)}>Include Author</label>
						</Checkbox>
					</div>

					<div className={css(styleSheet.formGroup)}>
						<label className={css(styleSheet.label)}>Author Name</label>
						<input
							type='text'
							value={authorName}
							onChange={e => onAuthorNameChange(e.target.value)}
							className={css(styleSheet.textarea, styleSheet.authorInput)}
							disabled={!includeAuthor}
							placeholder={includeAuthor ? 'Enter author name' : 'Enable "Include Author" to edit'}
						/>
					</div>
				</div>

				<div className={css(styleSheet.optionsSection)}>
					<div className={css(styleSheet.formGroup)}>
						<div className={css(styleSheet.labelContainer)}>
							<label className={css(styleSheet.label)}>Testimonial Text</label>
							<div className={css(styleSheet.charCount, isContentTooLong && styleSheet.charCountError)}>
								{reviewText.length}/{MAX_CONTENT_LENGTH}
							</div>
						</div>
						<textarea
							value={reviewText}
							onChange={e => onReviewTextChange(e.target.value)}
							className={css(styleSheet.textarea, isContentTooLong && styleSheet.textareaError)}
							placeholder='Enter your testimonial text here...'
						/>
						{isContentTooLong && (
							<div className={css(styleSheet.warningMessage)}>
								Content is too long. Please reduce the text to {MAX_CONTENT_LENGTH} characters or less.
							</div>
						)}
					</div>
				</div>
			</div>

			<Button onClick={onGenerateImage} disabled={isContentTooLong || createPostDisabled}>
				Create Post
			</Button>

			<UnifiedImageDialog
				showFreeImagesTab={false}
				initialImage={selectedBackgroundImage?.fileAttachment}
				initialTab={selectedBackgroundImage ? 'ai-edit' : 'select-image'}
				open={backgroundImageModalOpen}
				onOpenChange={setBackgroundImageModalOpen}
				onImageSelected={image => {
					setSelectedBackgroundImage({
						slug: 'ai-edited',
						fileAttachment: image,
					});
					setBackgroundImageModalOpen(false);
				}}
				showSelectImageTab
				onSelectImages={images => {
					setSelectedBackgroundImage(images[0]);
					setBackgroundImageModalOpen(false);
				}}
				allowMultipleSelect={false}
				imagesForSelection={IMAGES_FOR_SELECTION}
				selectImageTitle='Select a Background Image'
				selectImageSubtitle='Note: Images will have an overlayed color to help with readability. You can change this by editing with AI or applying your brand to the selected image.'
				onPreProcessSelectImage={async file =>
					addColorOverlayToImage(file, TESTIMONIAL_OVERLAY_COLOR, TESTIMONIAL_OVERLAY_OPACITY)
				}
			/>
		</div>
	);
};

export const TestimonialControls = inject(ImpersonationContextKey)(observer(_TestimonialControls));
